import { type ReactNode } from "react";
import { useLocation } from "wouter";
import { Coins } from "lucide-react";
import env from "@/env";
import { HardwareIcon } from "@/components/Icons/HardwareIcon";
import { useUserDashboardMenuVisibility } from "@/hooks/useUserDashboardMenuVisibility";
import { explorerPrefixedPath } from "@/utils";

const menuButtonClass =
  "subtext-small flex cursor-pointer flex-row items-center rounded px-4 py-[10px] text-gray-dark-1000 transition-colors hover:bg-container hover:text-foreground dark:hover:bg-gray-dark-1100";

export type UserDashboardMenuVisibility = {
  hasWorkers?: boolean;
  hasStaking?: boolean;
  isLoading: boolean;
};

type UserDashboardMenuItemsContentProps = {
  variant: "popover" | "mobile";
  onNavigate?: () => void;
  visibility: UserDashboardMenuVisibility;
};

type MenuLinkProps = {
  icon: ReactNode;
  label: string;
  href: string;
  variant: "popover" | "mobile";
  onNavigate?: () => void;
};

const MenuLink = ({ icon, label, href, variant, onNavigate }: MenuLinkProps) => {
  const [, setLocation] = useLocation();

  if (variant === "mobile") {
    return (
      <a
        href={href}
        className="subtext-small flex flex-row items-center gap-2 py-3 text-gray hover:text-foreground"
        onClick={(event) => {
          event.preventDefault();
          setLocation(href);
          onNavigate?.();
        }}
      >
        {icon}
        {label}
      </a>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={menuButtonClass}
      onClick={() => setLocation(href)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          setLocation(href);
        }
      }}
    >
      {icon}
      {label}
    </div>
  );
};

export const UserDashboardMenuItemsContent = ({
  variant,
  onNavigate,
  visibility
}: UserDashboardMenuItemsContentProps) => {
  const { hasWorkers, hasStaking, isLoading } = visibility;

  if ((!env.EXPLORER_COMBINED && !env.ALWAYS_SHOW_USER_DASHBOARD_MENUS) || isLoading) {
    return null;
  }

  return (
    <>
      {hasWorkers && (
        <MenuLink
          variant={variant}
          icon={<HardwareIcon className="mr-2 h-[18px] w-[18px]" />}
          label="My Worker Dashboard"
          href={explorerPrefixedPath("/worker/dashboard/devices")}
          onNavigate={onNavigate}
        />
      )}
      {hasStaking && (
        <MenuLink
          variant={variant}
          icon={
            variant === "popover" ? (
              <Coins className="mr-2 h-[18px] w-[18px]" />
            ) : (
              <Coins className="h-[18px] w-[18px]" />
            )
          }
          label="My Staking Dashboard"
          href={explorerPrefixedPath("/staking/my-co-staking")}
          onNavigate={onNavigate}
        />
      )}
    </>
  );
};

type UserDashboardMenuItemsProps = {
  isMenuOpen: boolean;
  variant: "popover" | "mobile";
  onNavigate?: () => void;
};

export const UserDashboardMenuItems = ({
  isMenuOpen,
  variant,
  onNavigate
}: UserDashboardMenuItemsProps) => {
  const visibility = useUserDashboardMenuVisibility(isMenuOpen);

  return (
    <UserDashboardMenuItemsContent
      variant={variant}
      onNavigate={onNavigate}
      visibility={visibility}
    />
  );
};
